<script setup lang="ts">
interface Stat {
  value: string;
  label: string;
}

interface Project {
  title: string;
  category: string;
  description: string;
  href: string;
  imageUrl: string;
}

const stats: Stat[] = [
  { value: "100+", label: "Critical Sites Supported" },
  { value: "40%", label: "Avg. Downtime Reduction" },
  { value: "ISO", label: "9001/14001/45001 Certified" },
];

const projects: Project[] = [
  {
    title: "Network & Wi-Fi Upgrade for Museum of Contemporary Art",
    category: "Cultural",
    description:
      "Enterprise-grade network infrastructure with Aruba switches, Cat6a and fibre cabling, and high-performance Wi-Fi across public areas and exhibits.",
    href: "/about-us/our-projects/network-infrastructure-wi-fi-upgrade-mca",
    imageUrl: "/img/projects/mca-network-upgrade-card.webp",
  },
  {
    title: "Integrated Security for St John Ambulance NSW",
    category: "Healthcare",
    description:
      "End-to-end solution integrating access control, CCTV, fire panel integration, and high-speed Wi-Fi with centralised management.",
    href:
      "/about-us/our-projects/integrated-security-network-infrastructure-wifi-solution-st-john-ambulance-nsw",
    imageUrl: "/img/projects/st-john-ambulance-nsw-card.webp",
  },
  {
    title: "Mobile Signal Booster for John Holland Tunnel",
    category: "Construction",
    description:
      "Cel-Fi mobile signal repeaters with high-gain antennas ensuring uninterrupted cellular communication throughout the tunnel.",
    href: "/about-us/our-projects/mobile-signal-booster-john-holland-mascot-tunnel",
    imageUrl: "/img/projects/john-holland-tunnel-card.webp",
  },
];

const clientLogos = [
  { name: "Thrifty Car Rental", logo: "/img/about-us/clients/logos/thrifty.webp" },
  { name: "Performing Lines", logo: "/img/about-us/clients/logos/performing-lines.webp" },
  { name: "Bell Partners", logo: "/img/about-us/clients/logos/bell-partners.webp" },
  { name: "Aveo Peninsula Gardens", logo: "/img/about-us/clients/logos/aveo.webp" },
  { name: "City of Moreton Bay", logo: "/img/about-us/clients/logos/moreton-bay-regional-council.webp" },
  { name: "Ozlem Foods", logo: "/img/about-us/clients/logos/ozlem-foods.webp" },
];
</script>

<template>
  <section class="bg-neutral-50 py-20 lg:py-24">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Section header -->
      <div class="text-center">
        <p class="text-sm font-semibold uppercase tracking-wider text-primary-400">
          Proof in the Results
        </p>
        <h2
          class="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
        >
          Results we've delivered
        </h2>
      </div>

      <!-- Stats row -->
      <div class="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3 lg:gap-8">
        <div
          v-for="stat in stats"
          :key="stat.label"
          class="rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-xs lg:p-8"
        >
          <p class="text-3xl font-extrabold text-primary-400 lg:text-4xl">
            {{ stat.value }}
          </p>
          <p class="mt-2 text-base leading-7 text-neutral-600">
            {{ stat.label }}
          </p>
        </div>
      </div>

      <!-- Project cards -->
      <div class="mt-16 grid gap-8 md:grid-cols-3">
        <div
          v-for="project in projects"
          :key="project.title"
          class="group relative flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-xs transition-all duration-300 hover:-translate-y-1"
        >
          <!-- Project image -->
          <div class="aspect-3/2 overflow-hidden">
            <ResponsiveImage
              :src="project.imageUrl"
              :alt="project.title"
              class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              :width="1200"
              :height="800"
            />
          </div>

          <!-- Content -->
          <div class="flex flex-1 flex-col p-6">
            <span
              class="inline-flex w-fit items-center rounded-full bg-primary-400/10 px-2.5 py-0.5 text-xs font-bold text-primary-400"
            >
              {{ project.category }}
            </span>
            <h3 class="mt-3 text-lg font-extrabold text-dark">
              {{ project.title }}
            </h3>
            <p class="mt-2 flex-1 text-base leading-7 text-neutral-600">
              {{ project.description }}
            </p>
          </div>

          <!-- Hover overlay link -->
          <NuxtLink
            :to="project.href"
            class="absolute inset-0"
            aria-label="View project"
          />

          <!-- Bottom accent line -->
          <div
            class="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-primary-400 transition-transform duration-300 group-hover:scale-x-100"
            aria-hidden="true"
          ></div>
        </div>
      </div>

      <!-- View all link -->
      <div class="mt-10 text-center">
        <NuxtLink
          to="/about-us/our-projects"
          class="inline-flex items-center gap-2 rounded-full border border-primary-400 px-5 py-2.5 font-bold text-primary-400 transition-all hover:bg-primary-400 hover:text-white"
        >
          View All Projects
          <Icon
            name="lucide:arrow-right"
            size="20"
            class="transition-transform group-hover:translate-x-0.5"
          />
        </NuxtLink>
      </div>

      <!-- Client logos -->
      <div class="mt-16 border-t border-neutral-200 pt-12">
        <p
          class="text-center text-sm font-semibold uppercase tracking-wider text-neutral-500"
        >
          Trusted by businesses across Australia
        </p>

        <!-- Logo grid -->
        <div class="mt-8 flex flex-wrap items-center justify-center gap-x-12 gap-y-8 lg:gap-x-16">
          <div
            v-for="logo in clientLogos"
            :key="logo.name"
            class="flex h-16 w-36 items-center justify-center transition-transform hover:scale-105"
          >
            <img
              :src="logo.logo"
              :alt="logo.name"
              class="max-h-full max-w-full object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
